import { useState } from 'react'
import { useSupabaseClient } from '@supabase/auth-helpers-react'

// Mantine
import { Avatar, PinInput, Anchor } from '@mantine/core'

// Axios
import axios from 'axios'

// Icons
import { IconX, IconArrowLeft, IconHistory } from '@tabler/icons-react'

import { BACKOFFICE_API_URL } from '@/lib/constants'

export default function QuickLogin({ profiles }) {
    const supabaseClient = useSupabaseClient()
    const lastUser = JSON.parse(localStorage.getItem('last_user') || 'null')

    // Handle pin input
    const [selectedProfile, setSelectedProfile] = useState(null)
    const [pin, setPin] = useState('')

    // Select profile
    const handleSelectProfile = (profile) => {
        setSelectedProfile(profile)
        setPin('')
    }

    // Is a standalone app
    const isStandalone = new Boolean(navigator.standalone)

    const handlePinInput = (value) => {
        const newPin = pin + value

        // Append value to pin if pin is less than 4 digits
        if (newPin.length <= 4) {
            setPin(newPin)
        }

        // If pin is 4 digits, submit pin
        if (newPin.length === 4) {
            handlePinSubmit(newPin)
        }
    }

    const handlePinChange = (value) => {
        setPin(value)
        if (value.length === 4) {
            handlePinSubmit(value)
        }
    }

    const clearPin = () => {
        setPin('')
    }

    const backPin = () => {
        setPin(pin.slice(0, -1))
    }

    // When pin is filled, send request to backend
    const handlePinSubmit = async (pinValue) => {
        setIsLoading(true)
        setError(null)

        try {
            const { data } = await axios.post(
                BACKOFFICE_API_URL + '/quick-login/login',
                {
                    pin: pinValue,
                    profile_id: selectedProfile.id,
                },
                { withCredentials: true }
            )

            if (data?.access_token && data?.refresh_token) {
                await supabaseClient.auth.setSession({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token,
                })
            }

            setIsLoading(false)
        } catch (error) {
            let errorMessage =
                error?.response?.data?.error_message ??
                'Une erreur est survenue'

            if (error?.response?.status === 429) {
                const retryAfter = error?.response?.data?.retry_after
                if (retryAfter && retryAfter >= 60) {
                    errorMessage = `Trop de tentatives, veuillez patienter ${Math.ceil(retryAfter / 60)} min`
                } else if (retryAfter) {
                    errorMessage = `Trop de tentatives, veuillez patienter ${retryAfter}s`
                } else {
                    errorMessage = 'Trop de tentatives, veuillez patienter'
                }
            }

            setIsLoading(false)
            setError(errorMessage)
            setPin('')
        }
    }

    // Handle loading and error
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    if (selectedProfile) {
        return (
            <div className="border bg-white p-5 rounded-xl flex flex-col items-center justify-center gap-5">
                <div className="flex flex-col items-center justify-center gap-2">
                    <Avatar color="gray" size={40}>
                        {selectedProfile?.short}
                    </Avatar>

                    <span className="text-gray-700">
                        {selectedProfile?.name}
                    </span>
                </div>

                <div className="flex flex-col items-center justify-center gap-2">
                    <p className="text-gray-500">
                        Veuillez entrer le code PIN de votre profil
                    </p>

                    <PinInput
                        value={pin}
                        onChange={handlePinChange}
                        length={4}
                        mask
                        type="number"
                        id="quick-login-pin"
                        disabled={isLoading}
                        inputMode="none"
                    />

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <div className="grid grid-cols-3 gap-3 mt-6 mb-4 w-[240px]">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
                            <button
                                key={number}
                                className="w-[72px] h-[72px] rounded-full text-xl font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center"
                                onClick={() => handlePinInput(number)}
                            >
                                {number}
                            </button>
                        ))}

                        <button
                            className="w-[72px] h-[72px] rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center"
                            onClick={clearPin}
                        >
                            <IconX size={20} />
                        </button>

                        <button
                            className="w-[72px] h-[72px] rounded-full text-xl font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center"
                            onClick={() => handlePinInput(0)}
                        >
                            0
                        </button>

                        <button
                            className="w-[72px] h-[72px] rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center"
                            onClick={backPin}
                        >
                            <IconArrowLeft size={20} />
                        </button>
                    </div>
                </div>

                <Anchor
                    component="button"
                    onClick={() => setSelectedProfile(null)}
                >
                    ← Retour à la selection
                </Anchor>
            </div>
        )
    }

    const lastProfile = lastUser
        ? profiles.find((p) => p.id === lastUser.id)
        : null

    return (
        <div className="flex flex-col bg-white rounded-xl border divide-y max-h-[50vh] overflow-y-auto">
            {profiles.map((profile) => (
                <div
                    key={profile.id}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => handleSelectProfile(profile)}
                >
                    <Avatar color="gray" size={32}>
                        {profile.short}
                    </Avatar>

                    <span className="text-sm text-gray-700">
                        {profile.name}
                    </span>

                    {lastProfile?.id === profile.id && (
                        <IconHistory size={16} className="ml-auto text-gray-400" />
                    )}
                </div>
            ))}
        </div>
    )
}
